import { useRef, useState, type CSSProperties } from 'react';
import { Button } from '../../components/Button';
import { Field } from '../../components/Field';
import { TimeSelect } from '../../components/TimeSelect';
import { SeriesOccurrenceConflictBanner } from '../../components/SeriesOccurrenceConflictBanner';
import { ApiError, seriesOccurrenceConflictDetail, shiftCoachSeriesTime } from '../../lib/api';
import { useToast } from '../../hooks/useToast';
import { PS, T } from '../../lib/theme';
import { POLICY, POLICY_MIN_TIME, POLICY_MAX_START_TIME } from '../../lib/policy';
import { occurrenceSkipLabel } from '../../lib/coach-series';
import { formatClock } from '../../lib/time';
import type {
  SeriesOccurrenceConflictDetail,
  SeriesOccurrenceSkip,
  SeriesTimeShiftResult,
} from '../../lib/types';

/** A plan, carried together with the window it describes — see `run`. */
interface Preview {
  start: string;
  end: string;
  result: SeriesTimeShiftResult;
}

interface Props {
  seriesId: number;
  /** The series' current window, "HH:MM" — the form starts here. */
  startTime: string;
  endTime: string;
  /** Called after a successful write, so the page can re-read the series. */
  onShifted: () => void;
}

/**
 * Move a whole coach series to a new same-day window (admin#48, api#84).
 *
 * The flow is the endpoint's own two-step: `dry_run=true` first, which runs the
 * identical server-side planning code *without writing* and reports exactly
 * which occurrences would move and which it would leave alone; then the same
 * body again, without the flag, to commit.
 *
 * What this component deliberately does NOT do is check the window. The
 * same-day rule, the minimum duration and the slot alignment all live in
 * `prospin-api`, and its 400 text is what staff read — verbatim, so a client
 * paraphrase can never drift from the rule it describes. (The two *create*
 * forms render `SlotWindowFields`, which does pre-check; the difference is
 * deliberate and recorded in .ai/features/48-series-time-shift/DECISIONS.md.)
 * The Preview button is disabled only while the form is incomplete.
 */
export function SeriesTimeShiftPanel({ seriesId, startTime, endTime, onShifted }: Props) {
  // A series ending at the club's closing time has no window this route can
  // express — see END_OF_DAY_NOTE. Bail before the form rather than render one
  // whose end select cannot even show the series' current value.
  if (endTime === END_OF_DAY) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={T.label}>Change time (same day)</span>
        <p style={{ ...T.caption, color: PS.textSec, margin: 0 }}>{END_OF_DAY_NOTE}</p>
      </div>
    );
  }

  return (
    <ShiftForm seriesId={seriesId} startTime={startTime} endTime={endTime} onShifted={onShifted} />
  );
}

/**
 * The club closes at 24:00 and `CreateCoachSeriesRequest` accepts it, but
 * `ShiftCoachSeriesTimeRequest.end_time` is pinned to `2[0-3]:[0-5]\d` — so a
 * 23:00–24:00 series is creatable and not shiftable, and sending 24:00 here is
 * a 422 whose FastAPI body is an array `messageFromDetail` cannot render as a
 * sentence ("Request failed: 422" is what staff would read).
 *
 * The client therefore never offers 24:00. Widening the API to match its own
 * create model is givip/prospin-api#115; when that lands, drop this and pass
 * `includeMax` to the end select.
 */
const END_OF_DAY = '24:00';
const END_OF_DAY_NOTE =
  'This series runs until the club closes at 24:00, and a series ending at closing time ' +
  'cannot be moved from here yet — the API rejects 24:00 on this route. Delete and recreate ' +
  'the series to change its window.';

function ShiftForm({ seriesId, startTime, endTime, onShifted }: Props) {
  const { push } = useToast();

  const [start, setStart] = useState(startTime);
  const [end, setEnd] = useState(endTime);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [conflict, setConflict] = useState<SeriesOccurrenceConflictDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  /**
   * Bumped by every request and every select edit, so a response arriving late
   * can tell whether it still answers the window on screen.
   */
  const requestSeq = useRef(0);

  /**
   * A preview describes one specific window. Any edit to the selects makes the
   * loaded preview a description of something else, so it — and the errors that
   * belong to it — go with it. Without this, staff could preview 19:00–20:00,
   * nudge the end to 21:00, and press an Apply button that no longer matches
   * what they were shown.
   *
   * The sequence bump covers the same mistake while the dry run is still open
   * and there is no loaded preview here to clear.
   */
  const changeWindow = (next: { start?: string; end?: string }) => {
    requestSeq.current += 1;
    if (next.start !== undefined) setStart(next.start);
    if (next.end !== undefined) setEnd(next.end);
    setPreview(null);
    setConflict(null);
    setError(null);
  };

  const incomplete = !start || !end;

  const run = async (dryRun: boolean) => {
    const seq = (requestSeq.current += 1);
    // Apply the window carried by the preview, never a newer control value that
    // the preview did not describe.
    const target = dryRun ? { start, end } : { start: preview!.start, end: preview!.end };
    setBusy(true);
    setConflict(null);
    setError(null);
    try {
      const body = { start_time: target.start, end_time: target.end };
      // The write path is the flag's *absence*, not `dry_run=false` — see the
      // wrapper. Two call sites rather than a computed options object so that
      // stays true at a glance.
      const result = dryRun
        ? await shiftCoachSeriesTime(seriesId, body, { dryRun: true })
        : await shiftCoachSeriesTime(seriesId, body);
      if (dryRun) {
        if (seq !== requestSeq.current) return;
        setPreview({ start: target.start, end: target.end, result });
        return;
      }
      // A successful write changed the server even if staff edited the selects
      // while it was in flight. Suppress stale copy, but always refetch.
      if (seq === requestSeq.current) {
        push(
          'success',
          `Series moved to ${formatClock(target.start)}–${formatClock(target.end)} · ` +
            `${result.moved.length} occurrence${result.moved.length === 1 ? '' : 's'} updated.`,
        );
      }
      setPreview(null);
      onShifted();
    } catch (err) {
      if (seq !== requestSeq.current) return;
      const seriesConflict = seriesOccurrenceConflictDetail(err);
      if (seriesConflict) {
        setConflict(seriesConflict);
      } else {
        setError(err instanceof ApiError ? err.message : 'Time shift failed');
      }
      // A rejected apply invalidates the preview it came from: the world
      // changed under it (someone else took the slot), so re-preview.
      setPreview(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <span style={T.label}>Change time (same day)</span>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <Field label="New start time">
          <TimeSelect
            value={start}
            onChange={(v) => changeWindow({ start: v })}
            min={POLICY_MIN_TIME}
            max={POLICY_MAX_START_TIME}
            stepMinutes={POLICY.SLOT_STEP_MINUTES}
            ariaLabel="New start time"
          />
        </Field>
        <Field label="New end time">
          <TimeSelect
            value={end}
            onChange={(v) => changeWindow({ end: v })}
            min={POLICY_MIN_TIME}
            // No `includeMax`: 24:00 is a 422 on this route, unlike the create
            // forms' `SlotWindowFields`. See END_OF_DAY_NOTE / api#115.
            max={END_OF_DAY}
            stepMinutes={POLICY.SLOT_STEP_MINUTES}
            ariaLabel="New end time"
          />
        </Field>
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <Button variant="secondary" onClick={() => void run(true)} disabled={busy || incomplete}>
          {busy && !preview ? 'Checking…' : 'Preview change'}
        </Button>
        {preview && (
          <>
            <Button onClick={() => void run(false)} disabled={busy}>
              {busy ? 'Applying…' : 'Apply shift'}
            </Button>
            <Button variant="secondary" onClick={() => setPreview(null)} disabled={busy}>
              Discard preview
            </Button>
          </>
        )}
      </div>

      <p style={{ ...T.caption, color: PS.textSec, margin: 0 }}>
        Preview first — nothing is written until you apply. Occurrences that have already started,
        that were cancelled, or that someone re-timed by hand keep their own time.
      </p>

      {error && (
        <div role="alert" style={{ ...T.caption, color: PS.errorText }}>
          {error}
        </div>
      )}

      {conflict && <SeriesOccurrenceConflictBanner detail={conflict} />}

      {preview && <PreviewSummary result={preview.result} />}
    </div>
  );
}

function PreviewSummary({ result }: { result: SeriesTimeShiftResult }) {
  const { moved, skipped } = result;

  return (
    <div
      style={{
        border: `1px solid ${PS.borderLight}`,
        borderRadius: 3,
        padding: 12,
        background: PS.surfaceDisabled,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        // A long open-ended series previews many dates; scroll rather than
        // push the rest of the page off screen.
        maxHeight: 320,
        overflowY: 'auto',
      }}
    >
      <div style={{ ...T.subheading, color: PS.nearBlack }}>
        {moved.length} occurrence{moved.length === 1 ? '' : 's'} will move
      </div>
      {moved.length === 0 ? (
        <div style={{ ...T.caption, color: PS.textSec }}>
          Nothing to move — every upcoming occurrence keeps its own time. Applying still updates the
          series itself, so occurrences materialized later use the new window.
        </div>
      ) : (
        <ul style={{ margin: 0, paddingLeft: 18 }}>
          {moved.map((m) => (
            <li key={m.booking_id} style={{ ...T.caption, color: PS.nearBlack }}>
              <span style={dateCell}>{m.date}</span> → {formatClock(m.start_time)}–
              {formatClock(m.end_time)} <code>#{m.booking_id}</code>
            </li>
          ))}
        </ul>
      )}

      {skipped.length > 0 && (
        <>
          <div style={{ ...T.subheading, color: PS.nearBlack }}>
            {skipped.length} occurrence{skipped.length === 1 ? '' : 's'} stay
            {skipped.length === 1 ? 's' : ''} where {skipped.length === 1 ? 'it is' : 'they are'}
          </div>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {skipped.map((s: SeriesOccurrenceSkip) => (
              <li key={s.booking_id} style={{ ...T.caption, color: PS.textSec }}>
                <span style={dateCell}>{s.date}</span> {formatClock(s.start_time)}–
                {formatClock(s.end_time)} · <span>{occurrenceSkipLabel(s.reason)}</span>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

const dateCell: CSSProperties = { fontFamily: "'Barlow',sans-serif" };
